import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Checkbox, Col, Row } from 'antd';
import '../../../styles/savers/custom.css';

import { uniqBy } from 'es-toolkit';
import moment from 'moment';
import { ArrowRightOutlined } from '@ant-design/icons';

import { exportSaversDataToExcel } from '@/utils/excel';
import { getTrailerStatusColor } from '@/views/screens/Savers/utils';

import { setAppLoader } from '../../../redux/common/CommonActions';
import { setLocationRequestId, setTrailerId, setTrailerRequestId } from '../../../redux/Savers/SaversActions';
import { getTrailerRequestByTrailerIDEffect, getTrailersListEffect } from '../../../redux/Savers/SaversEffects';
import SecondaryLabel from '../../components/Labels/SecondaryLabel';
import AlertModal from '../../components/Modals/AlertModal';
import ScrollPagination from '../../components/Paginations/ScrollPagination';
import ContentSpinner from '../../components/Spinners/ContentSpinner';
import TrailerSearch from '../../screens/Savers/TrailerDatabase/TrailerSearch';
import { TRAILER_STATUS_CODE } from './TrailerManagement/constants';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from './trailer-request.constants';

export const dateFormatter = (cell) => {
  if (cell) {
    const newDate = moment(cell).format('MMM D YYYY');
    return <span>{newDate}</span>;
  }
};

const TRAILER_ERROR_MESSAGE =
  'Trailer request has not been created for this trailer. Please create trailer request to continue.';

const bgColors = {
  Assigned: '#F6BB42',
  Broken: '#E9573F',
  Planned: '#825B5B',
  Unassigned: '#00B1E1',
  Unconfirmed: '#825B5B',
};

const getStyle = (cell, bgColors) => {
  if (!bgColors || !cell) {
    return {};
  }

  if (cell === 'Planned') {
    return {
      backgroundColor: bgColors.Planned,
      color: '#FFFFFF',
    };
  }
  if (cell === 'Broken') {
    return {
      backgroundColor: bgColors.Broken,
      color: '#FFFFFF',
    };
  }
  if (cell === 'Unconfirmed') {
    return {
      backgroundColor: bgColors.Unconfirmed,
      color: '#FFFFFF',
    };
  }
  if (cell === 'Assigned') {
    return {
      backgroundColor: bgColors.Assigned,
      color: '#FFFFFF',
    };
  }
  if (cell === 'Unassigned') {
    return {
      backgroundColor: bgColors.Unassigned,
      color: '#FFFFFF',
    };
  }

  return {
    backgroundColor: '#ffff',
    color: 'black',
  };
};

export default function TrailerDatabase({ isModal, handleTrailerData, status }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();
  const locationId = useSelector((state) => state.savers.locID.data);
  const branchKey = location?.state?.branchKey || locationId;
  const { selectedCompany } = useSelector((state) => state.auth);
  const userRole = useSelector((state) => state.savers.loggedInUser.data);
  const [loading, setLoading] = useState(false);
  const [errMessage, setErrMessage] = useState('');
  const [trailerOptions, setTrailerOptions] = useState([]);
  const [tempTrailerOptions, setTempTrailerOptions] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [selectedTrailerId, setSelectedTrailerId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [trailerParams, setTrailerParams] = useState('');
  const scrollRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: Add dependencies* that are required
  useEffect(() => {
    if (!isModal) {
      getTrailerList(branchKey);
    } else {
      const code = status === 'Loaded' ? TRAILER_STATUS_CODE.Loaded : TRAILER_STATUS_CODE.Empty;
      getTrailerList('', '', code, '');
    }
  }, [selectedCompany]);

  const onAlertConfirm = () => {
    setAlertOpen(false);
  };

  const handleGeneralSearch = (searchTerm) => {
    const filteredOptions = trailerOptions.filter((option) =>
      Object.values(option).some(
        (value) => typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
    setTempTrailerOptions(filteredOptions);
  };

  const getTrailerList = async (branchKey, trailerId, statuscode, locationType, reset) => {
    try {
      setLoading(true);
      const params = {};
      if (branchKey) {
        params.branchKey = branchKey;
      }
      if (trailerId) {
        params.trailerId = trailerId;
      }
      if (statuscode) {
        params.status = statuscode;
      }
      if (locationType) {
        params.locationType = locationType;
        if (locationType === 'SZ9677') {
          params.regionDesc = 'yard';
        }
      }
      if (isModal) {
        if (statuscode === 'LOD') {
          params.isShowIMSLocation = true;
        }
        params.regionDesc = 'yard';
      }
      if (reset) {
        setCurrentPage(1);
      }
      setTrailerParams(params);
      params.PageSize = DEFAULT_PAGE_SIZE;
      params.PageNumber = reset ? DEFAULT_PAGE_NUMBER : currentPage;
      const { data } = await getTrailersListEffect(params);

      setTrailerOptions(data);
      setTempTrailerOptions(data);
      if (data.length === 0) {
        setErrMessage('Data not found.');
      } else {
        setErrMessage('');
      }
      dispatch(setLocationRequestId(''));
      setLoading(false);
    } catch (error) {
      console.error(error);
      setTrailerOptions([]);
      setTempTrailerOptions([]);
      setLoading(false);
    }
  };

  const columnFormatter = () => {
    return <ArrowRightOutlined />;
  };

  const rollDoorCheckbox = (val) => {
    return <Checkbox checked={val} disabled />;
  };

  const handleSelectTrailer = (trailerId, row) => {
    setSelectedTrailerId(trailerId);
    handleTrailerData({ trailerData: row, trailerId });
  };

  const SelectTrailer = (trailerId, row) => {
    return (
      <Checkbox
        value={trailerId}
        checked={selectedTrailerId === trailerId}
        onChange={() => handleSelectTrailer(trailerId, row)}
        disabled={row.trailerStatusID === 'PLN'}
      />
    );
  };

  const columns = [
    {
      dataField: 'trailerID',
      hidden: true,
      text: 'ID',
    },
    {
      align: 'center',
      dataField: 'trailerID',
      formatter: (val, row) => SelectTrailer(val, row),
      headerStyle: { textAlign: 'center' },
      hidden: !isModal,
      sort: true,
      text: 'Select',
    },
    {
      align: 'left',
      dataField: 'trailerNumber',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Trailer #',
    },
    {
      align: 'right',
      dataField: 'unitSize',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Trailer Size',
    },
    {
      align: 'left',
      dataField: 'branchDesc',
      headerStyle: { textAlign: 'center', width: '250px' },
      sort: true,
      text: 'Location Name',
    },
    {
      align: 'right',
      dataField: 'branchCustRef',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Location ID',
    },
    {
      align: 'right',
      dataField: 'vendorId',
      headerStyle: { textAlign: 'center' },
      text: 'Vendor Id',
    },
    {
      align: 'right',
      dataField: 'locationType',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Supply Zone',
    },
    {
      align: 'center',
      dataField: 'rollDoor',
      formatter: (val) => rollDoorCheckbox(val),
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Roll Door',
    },
    {
      align: 'left',
      dataField: 'loadedDate',
      formatter: (col, row) => (row.statusDesc === 'Empty' ? '' : dateFormatter(col, row)),
      headerStyle: { textAlign: 'center', width: '150px' },
      text: 'Load Date',
    },
    {
      align: 'left',
      dataField: 'statusDesc',
      headerStyle: { textAlign: 'center' },
      hidden: true,
      sort: true,
      text: 'Status',
    },
    {
      align: 'left',
      dataField: 'yardName',
      headerStyle: { textAlign: 'center' },
      hidden: true,
      sort: true,
      text: 'Yard',
    },
    {
      align: 'left',
      dataField: 'storageDate',
      formatter: dateFormatter,
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'In Storage Date',
    },
    {
      align: 'left',
      dataField: 'eta',
      formatter: dateFormatter,
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'ETA',
    },

    {
      align: 'left',
      dataField: 'latestDeliveryLocationName',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'To Location',
    },
    {
      align: 'left',
      dataField: 'soft',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Soft Weight',
    },
    {
      align: 'left',
      dataField: 'softOks',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Soft Oks',
    },
    {
      align: 'left',
      dataField: 'misc',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Misc Weight',
    },
    {
      align: 'left',
      dataField: 'miscOKs',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Misc OKs',
    },
    {
      align: 'left',
      dataField: 'book',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Book Weight',
    },
    {
      align: 'left',
      dataField: 'bookOks',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Book Oks',
    },
    {
      align: 'left',
      dataField: 'furn',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Furn',
    },
    {
      align: 'left',
      dataField: 'oli',
      headerStyle: { textAlign: 'center' },
      sort: true,
      text: 'Oli',
    },
    {
      align: 'left',
      dataField: 'trailerStatus',
      headerStyle: { textAlign: 'center' },
      hidden: true,
      sort: true,
      style: (cell) => {
        if (cell === '' || cell === null) {
          return {};
        }
        if (cell === 'Planned') {
          return {
            backgroundColor: bgColors.Planned,
            color: '#FFFFFF',
          };
        }
        if (cell === 'Broken') {
          return {
            backgroundColor: bgColors.Broken,
            color: '#FFFFFF',
          };
        }
        if (cell === 'Unconfirmed') {
          return {
            backgroundColor: bgColors.Unconfirmed,
            color: '#FFFFFF',
          };
        }
        if (cell === 'Assigned') {
          return {
            backgroundColor: bgColors.Assigned,
            color: '#FFFFFF',
          };
        }
        if (cell === 'Unassigned') {
          return {
            backgroundColor: bgColors.Unassigned,
            color: '#FFFFFF',
          };
        }

        return {
          backgroundColor: '#c8e6c9',
          color: '#FFFFFF',
        };
      },
      text: 'CT Trailer Status',
    },
    {
      align: 'left',
      dataField: 'unitRentalCompany',
      formatter: (cell) => (cell === 'NULL' ? '' : cell),
      headerStyle: { textAlign: 'center' },
      text: 'Rental Company',
    },
    {
      align: 'center',
      dataField: 'isOutOfService',
      formatter: (val) => rollDoorCheckbox(val),
      headerStyle: { textAlign: 'center' },
      hidden: isModal,
      sort: true,
      text: "Don't Use",
    },
    {
      align: 'center',
      dataField: 'notInUseReason',
      headerStyle: { textAlign: 'center' },
      hidden: isModal,
      sort: true,
      text: 'Comment',
    },
    {
      align: 'center',
      dataField: 'request',
      events: {
        onClick: (_e, _column, _columnIndex, row) => {
          loadTrailerRequestData(row.trailerID);
        },
      },
      formatter: columnFormatter,
      headerStyle: { textAlign: 'center' },
      hidden: isModal,
      text: 'Go to Trailer Requests',
    },
  ];

  const loadTrailerRequestData = async (trailerID) => {
    dispatch(setAppLoader(true));
    try {
      const { data } = await getTrailerRequestByTrailerIDEffect(trailerID);
      if (data?.length > 0) {
        if (data[0].trailerRequestData?.length > 0) {
          setAlertOpen(false);
          dispatch(setTrailerId(trailerID));
          dispatch(setTrailerRequestId(0));
          navigate('/Trailer-Review');
        } else {
          setAlertOpen(true);
        }
      } else {
        setAlertOpen(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const handleExport = () => {
    const filteredColumns = columns.filter(
      (column) => column.dataField !== 'request' && column.dataField !== 'trailerID',
    );

    exportSaversDataToExcel(trailerOptions, 'Trailer_Data', filteredColumns);
  };

  const handleNextPage = useCallback(async () => {
    if (scrollRef.current) {
      return;
    }
    try {
      scrollRef.current = true;
      setErrMessage('Loading more...');

      const nextPage = currentPage + 1;
      const params = {
        ...trailerParams,
        PageNumber: nextPage,
        PageSize: DEFAULT_PAGE_SIZE,
      };

      const { data } = await getTrailersListEffect(params);

      if (data.length === 0) {
        setErrMessage('No more data available.');
        return;
      }

      setErrMessage('');
      setTrailerOptions((prev) => {
        const combined = [...prev, ...data];
        return uniqBy(combined, ({ trailerID }) => trailerID);
      });

      setTempTrailerOptions((prev) => {
        const combined = [...prev, ...data];
        return uniqBy(combined, ({ trailerID }) => trailerID);
      });

      setCurrentPage(nextPage);
      dispatch(setLocationRequestId(''));
    } catch (error) {
      console.error('Error loading more data:', error);
      setErrMessage(`Error loading data: ${error.message}`);
    } finally {
      scrollRef.current = false;
    }
  }, [currentPage, trailerParams, dispatch]);

  return (
    <>
      {!isModal && (
        <Row gutter={20}>
          <Col sm={24}>
            <span className="pull-right color-1" style={{ marginTop: '-26px', position: 'fixed', right: '32px' }}>
              {' '}
              <strong> {userRole} </strong> Logged In{' '}
            </span>
          </Col>
        </Row>
      )}
      <div className="paper-container h-100 w-100">
        <div className="py-5 px-10">
          <AlertModal
            title="Alert"
            open={alertOpen}
            description={TRAILER_ERROR_MESSAGE}
            onOk={onAlertConfirm}
            onClose={onAlertConfirm}
          />
          <TrailerSearch
            branchKey={branchKey}
            isModal={isModal}
            onSearchClick={getTrailerList}
            onGeneralSearch={handleGeneralSearch}
            status={status}
          />
          {loading ? (
            <div style={{ marginTop: '15%' }}>
              <ContentSpinner size="large" />
            </div>
          ) : (
            <Row gutter={15}>
              <Col sm={24}>
                <div className="savers">
                  <div className="mx-1 mb-4  d-flex w-full justify-content-between align-items-end">
                    <div className="mx-1 fw-800">
                      {location.state?.branchDesc &&
                        location.state?.locationType &&
                        `${location.state.branchDesc} - ${location.state.locationType}`}
                    </div>
                    {!isModal && (
                      <button type="button" className="btn btn-dipblue" onClick={handleExport}>
                        Export to Excel
                      </button>
                    )}
                  </div>
                  <ScrollPagination
                    className="background-white br-4 w-100 h-100 scrollable-table-container horizontal vertical"
                    scrollOffset={50}
                    loadMore={handleNextPage}
                  >
                    <table className="w-100 background-white br-4">
                      <thead className=" ">
                        <tr>
                          {columns.map(
                            ({ text, hidden }) =>
                              !hidden && (
                                <th key={text} className="background-header text-white fixed fixed-top fixed-left">
                                  {text}
                                </th>
                              ),
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {tempTrailerOptions.length === 0 ? (
                          <tr>
                            <td colSpan={columns.length} style={{ color: 'red', textAlign: 'center' }}>
                              {errMessage}
                            </td>
                          </tr>
                        ) : (
                          (isModal ? tempTrailerOptions.filter((row) => !row.isOutOfService) : tempTrailerOptions).map(
                            (row) => (
                              <tr key={row.trailerID}>
                                <GenerateColumnValues
                                  row={row}
                                  columns={columns}
                                  bgColors={bgColors}
                                  getStyle={getStyle}
                                  isModal={isModal}
                                />
                              </tr>
                            ),
                          )
                        )}
                      </tbody>
                    </table>
                  </ScrollPagination>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </>
  );
}

const GenerateColumnValues = ({ row, columns, bgColors, getStyle }) => {
  const rowStyle = {
    backgroundColor: row.isOutOfService ? '#f80707ff' : getTrailerStatusColor(row.trailerStatusID),
  };

  return (
    <>
      {columns.map(
        ({ dataField, formatter, hidden, align, events }) =>
          !hidden && (
            <td
              key={dataField}
              className={`text-${align || 'left'}`}
              style={{ ...getStyle(row[dataField], bgColors), ...rowStyle }}
            >
              <SecondaryLabel className="f-15 fw-600 text-black">
                {events?.onClick ? (
                  <Link to="#" onClick={(e) => events.onClick(e, null, null, row, null)}>
                    {formatter ? formatter(row[dataField], row) : row[dataField]}
                  </Link>
                ) : formatter ? (
                  formatter(row[dataField], row)
                ) : (
                  row[dataField]
                )}
              </SecondaryLabel>
            </td>
          ),
      )}
    </>
  );
};
